import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/deploy-logs.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-config',
  templateUrl: './config.component.html',
  styleUrls: ['./config.component.scss']
})
export class ConfigComponent implements OnInit {
  externalTools = [
    {
      name: 'Jenkins',
      endpoint: 'https://jenkins.bizzycar.com',
      description: 'CI/CD Pipeline Management',
      icon: '⚙️'
    },
    {
      name: 'SonarQube',
      endpoint: 'https://sonarqube.bizzycar.com',
      description: 'Code Quality Analysis',
      icon: '📊'
    },
    {
      name: 'Datadog',
      endpoint: 'https://app.datadoghq.com',
      description: 'Monitoring and Analytics',
      icon: '📈'
    }
  ];

  configScripts = [
    {
      name: 'Display SSH commands',
      link: '/list-ssh',
      icon: '🔑',
      autoExecute: true
    },
    {
      name: 'Get slack config',
      link: '/which-slack-endpoint',
      icon: '💬',
      autoExecute: true
    },
    {
      name: 'Run Cypress on Branch',
      link: '/run-cypress',
      icon: '🧪'
    },
    {
      name: 'List CodeBuild Projects',
      link: '/list-build-projects',
      icon: '🏗️',
      autoExecute: true
    },
    {
      name: 'Get CodeBuild log for project',
      link: '/get-build-logs',
      icon: '📋'
    },
    {
      name: 'Get all healthchecks',
      link: '/get-all-healthchecks',
      icon: '❤️',
      autoExecute: true
    }
  ];

  showResponseModal = false;
  currentEndpoint = '';
  currentScriptName = '';
  responseData: SafeHtml = '';
  isLoading = false;
  error = '';
  codeBuildProjects: string[] = [];
  isCodeBuildLoading = false;
  codeBuildError = '';
  sshCommands: string = '';
  isSshLoading = false;
  sshError = '';
  slackConfig: string = '';
  isSlackLoading = false;
  slackError = '';
  healthChecks: string = '';
  isHealthChecksLoading = false;
  healthChecksError = '';
  selectedProject: string = '';

  constructor(
    private apiService: ApiService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    // Auto-execute CodeBuild projects list, SSH commands, Slack config, and healthchecks
    this.loadCodeBuildProjects();
    this.loadSshCommands();
    this.loadSlackConfig();
    this.loadHealthChecks();
  }

  loadCodeBuildProjects() {
    const codeBuildScript = this.configScripts.find(script => script.name === 'List CodeBuild Projects');
    if (codeBuildScript) {
      this.isCodeBuildLoading = true;
      this.codeBuildError = '';
      this.codeBuildProjects = [];

      this.apiService.makeApiCall(codeBuildScript.link).subscribe({
        next: (response) => {
          try {
            // Remove any HTML tags and trim the response
            const cleanResponse = response.replace(/<[^>]*>/g, '').trim();
            // Parse the cleaned response as JSON
            const projects = JSON.parse(cleanResponse);
            if (Array.isArray(projects)) {
              this.codeBuildProjects = projects;
            } else {
              throw new Error('Response is not an array');
            }
          } catch (err) {
            console.error('Parse error:', err);
            this.codeBuildError = 'Failed to parse CodeBuild projects.';
          }
          this.isCodeBuildLoading = false;
        },
        error: (err) => {
          console.error('API error:', err);
          this.codeBuildError = 'Failed to load CodeBuild projects. Please try again.';
          this.isCodeBuildLoading = false;
        }
      });
    }
  }

  loadSshCommands() {
    const sshScript = this.configScripts.find(script => script.name === 'Display SSH commands');
    if (sshScript) {
      this.isSshLoading = true;
      this.sshError = '';
      this.sshCommands = '';

      this.apiService.makeApiCall(sshScript.link).subscribe({
        next: (response) => {
          // Format the response to handle <br> tags
          const formattedResponse = response
            .replace(/<br>/g, '\n')  // Replace <br> with newlines
            .replace(/<[^>]*>/g, '') // Remove any other HTML tags
            .trim();                 // Remove extra whitespace
          this.sshCommands = formattedResponse;
          this.isSshLoading = false;
        },
        error: (err) => {
          console.error('SSH API error:', err);
          this.sshError = 'Failed to load SSH commands. Please try again.';
          this.isSshLoading = false;
        }
      });
    }
  }

  loadSlackConfig() {
    const slackScript = this.configScripts.find(script => script.name === 'Get slack config');
    if (slackScript) {
      this.isSlackLoading = true;
      this.slackError = '';
      this.slackConfig = '';

      this.apiService.makeApiCall(slackScript.link).subscribe({
        next: (response) => {
          // Format the response to handle <br> tags
          const formattedResponse = response
            .replace(/<br>/g, '\n')  // Replace <br> with newlines
            .replace(/<[^>]*>/g, '') // Remove any other HTML tags
            .trim();                 // Remove extra whitespace
          this.slackConfig = formattedResponse;
          this.isSlackLoading = false;
        },
        error: (err) => {
          console.error('Slack API error:', err);
          this.slackError = 'Failed to load Slack config. Please try again.';
          this.isSlackLoading = false;
        }
      });
    }
  }

  loadHealthChecks() {
    const healthChecksScript = this.configScripts.find(script => script.name === 'Get all healthchecks');
    if (healthChecksScript) {
      this.isHealthChecksLoading = true;
      this.healthChecksError = '';
      this.healthChecks = '';

      this.apiService.makeApiCall(healthChecksScript.link).subscribe({
        next: (response) => {
          try {
            // Format the response for better display
            const formattedResponse = response
              .replace(/<br>/g, '\n')  // Replace <br> with newlines
              .replace(/<[^>]*>/g, '') // Remove any other HTML tags
              .trim();                 // Remove extra whitespace

            // Process the response to add status colors
            const lines = formattedResponse.split('\n');
            const processedLines = lines.map(line => {
              // Add status colors
              if (line.includes('HTTP Status: 200')) {
                return line.replace('HTTP Status: 200', '<span class="status-200">HTTP Status: 200</span>');
              } else if (line.includes('HTTP Status: 4')) {
                return line.replace(/HTTP Status: 4\d{2}/, '<span class="status-4xx">$&</span>');
              } else if (line.includes('HTTP Status: 5')) {
                return line.replace(/HTTP Status: 5\d{2}/, '<span class="status-5xx">$&</span>');
              } else if (line.includes('HTTP Status:')) {
                return line.replace(/HTTP Status: \d{3}/, '<span class="status-other">$&</span>');
              }
              return line;
            });

            this.healthChecks = processedLines.join('\n');
          } catch (err) {
            console.error('HealthChecks formatting error:', err);
            this.healthChecks = response;
          }
          this.isHealthChecksLoading = false;
        },
        error: (err) => {
          console.error('HealthChecks API error:', err);
          this.healthChecksError = 'Failed to load healthchecks. Please try again.';
          this.isHealthChecksLoading = false;
        }
      });
    }
  }

  onProjectSelect(project: string) {
    this.selectedProject = project;
  }

  executeScript(script: any) {
    this.currentEndpoint = script.link;
    this.currentScriptName = script.name;
    this.showResponseModal = true;
    this.isLoading = true;
    this.error = '';
    this.responseData = '';

    this.apiService.makeApiCall(this.currentEndpoint).subscribe({
      next: (response) => {
        this.responseData = this.sanitizer.bypassSecurityTrustHtml(response);
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to execute script. Please try again.';
        this.isLoading = false;
      }
    });
  }

  closeResponseModal() {
    this.showResponseModal = false;
    this.currentEndpoint = '';
    this.currentScriptName = '';
    this.responseData = '';
    this.error = '';
  }

  copyResponse() {
    // Get the text content from the response div
    const responseElement = document.querySelector('.response-content div');
    if (responseElement) {
      const textToCopy = responseElement.textContent || '';
      if ((navigator as any).clipboard && window.isSecureContext) {
        (navigator as any).clipboard.writeText(textToCopy).then(() => {
          // Optional: Show a success message
          const copyButton = document.querySelector('.copy-button');
          if (copyButton) {
            const originalText = copyButton.innerHTML;
            copyButton.innerHTML = '<span class="copy-icon">✓</span> Copied!';
            setTimeout(() => {
              copyButton.innerHTML = originalText;
            }, 2000);
          }
        }).catch(err => {
          console.error('Failed to copy text: ', err);
        });
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          const copyButton = document.querySelector('.copy-button');
          if (copyButton) {
            const originalText = copyButton.innerHTML;
            copyButton.innerHTML = '<span class="copy-icon">✓</span> Copied!';
            setTimeout(() => {
              copyButton.innerHTML = originalText;
            }, 2000);
          }
        } catch (err) {
          console.error('Failed to copy text: ', err);
        }
        textArea.remove();
      }
    }
  }

  copyProjectName(projectName: string) {
    if ((navigator as any).clipboard && window.isSecureContext) {
      (navigator as any).clipboard.writeText(projectName).then(() => {
        // Show success message
        const button = document.querySelector(`[data-project="${projectName}"]`);
        if (button) {
          const originalText = button.innerHTML;
          button.innerHTML = '✓';
          setTimeout(() => {
            button.innerHTML = originalText;
          }, 2000);
        }
      }).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      // Fallback for non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = projectName;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        const button = document.querySelector(`[data-project="${projectName}"]`);
        if (button) {
          const originalText = button.innerHTML;
          button.innerHTML = '✓';
          setTimeout(() => {
            button.innerHTML = originalText;
          }, 2000);
        }
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
      textArea.remove();
    }
  }

  openExternalLink(url: string): void {
    window.open(url, '_blank');
  }
}
